import { z } from 'zod';
import {
  CALL_LIMITS,
  callDeclineReasonSchema,
  callEndReasonSchema,
  callKindSchema,
  callMediaSchema,
  callParticipantRoleSchema,
  callRingStatusSchema,
  callStatusSchema,
  callUnavailableReasonSchema,
} from '../enums/calls';
import {
  callGrantProfileSchema,
  callIdentityKindSchema,
  meetingRoomKindSchema,
} from '../enums/calendar';
import { isoDateTimeSchema, uuidSchema } from './common';
import { paginated, paginationQuerySchema } from './pagination';

/**
 * Calls and huddles (Phase 6, PLAN §4 "Later modules — Calls and huddles", D-026, D-044). Owned by
 * the `calls` API module (apps/api/src/modules/calls); the web client wraps these in
 * apps/web/src/api/calls.ts. Media runs through LiveKit; the API only issues grants.
 *
 *   GET    /calls/availability                       whether calls can be used here, and why not
 *   POST   /calls                                    start (rings in DMs and group DMs, announces in channels)
 *   GET    /calls/:callId
 *   POST   /calls/:callId/{cancel,accept,decline,ring,leave,end,token}
 *   GET    /conversations/:conversationId/calls      call history of one conversation
 *   GET    /me/calls                                 my call log (`status=missed` for missed calls)
 *   GET    /conversations/:conversationId/huddle     the live huddle, or null
 *   POST   /conversations/:conversationId/huddle/{join,leave}
 *   GET|PATCH /admin/settings/calls                  `calls.enabled`, the participant cap, the ring timeout
 *   POST   /integrations/livekit/webhook             LiveKit's signed webhooks (not a client route)
 *
 * Recording (`/calls/:id/recording*`) is not part of this contract: it is out of scope for the
 * first calls release (D-647), and `CallDto.recording` is therefore always null for now.
 */

// ---------------------------------------------------------------- the call

/** One person's part in a call. A huddle's "who is in it now" is `inCall`. */
export const callParticipantDtoSchema = z.object({
  userId: uuidSchema,
  role: callParticipantRoleSchema,
  /** Null for people who joined a huddle or channel call without being rung. */
  ringStatus: callRingStatusSchema.nullable(),
  /** First time they connected to the room. */
  joinedAt: isoDateTimeSchema.nullable(),
  /** The last time they left; null while connected or if they never joined. */
  leftAt: isoDateTimeSchema.nullable(),
  /** At least one of their devices is connected right now (`active_connections > 0`). */
  inCall: z.boolean(),
});
export type CallParticipantDto = z.infer<typeof callParticipantDtoSchema>;

/** Recording state; always null in this release (recording is not built, D-647). */
export const callRecordingStateSchema = z.object({
  active: z.boolean(),
  startedBy: uuidSchema.nullable(),
});

export const callDtoSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  kind: callKindSchema,
  media: callMediaSchema,
  status: callStatusSchema,
  startedBy: uuidSchema,
  startedAt: isoDateTimeSchema,
  /** First accept (calls) — null for huddles and for calls nobody answered. */
  answeredAt: isoDateTimeSchema.nullable(),
  endedAt: isoDateTimeSchema.nullable(),
  endReason: callEndReasonSchema.nullable(),
  /** `endedAt − coalesce(answeredAt, startedAt)`, set when the call ends. */
  durationSeconds: z.number().int().nonnegative().nullable(),
  /**
   * Members and guests at the busiest moment. Up to `CALL_LIMITS.webinarMaxParticipants` (510) for
   * a webinar room; every other call stays within its cap of at most 50 (D-1031).
   */
  peakParticipants: z.number().int().nonnegative().max(CALL_LIMITS.webinarMaxParticipants),
  participants: z.array(callParticipantDtoSchema),
  recording: callRecordingStateSchema.nullable(),
  /** The call's one system message in the conversation (D-039); null until it is written. */
  summaryMessageId: uuidSchema.nullable(),
  /**
   * Wave 5 (D-1026): a calendar meeting's call ("meeting room" in code; "meeting call" in the UI):
   * the meeting type of its occurrence, else null. A meeting room never rings, never ends when one
   * side leaves, and its state reaches people outside the host conversation through
   * `meeting.room.updated`.
   */
  meetingRoom: meetingRoomKindSchema.nullable(),
  /** The meeting occurrence the call belongs to (PTSTDM meeting calls have it too), else null. */
  meetingOccurrenceId: uuidSchema.nullable(),
  /** A webinar room's explicit cap (`calls.participant_cap`, 2..510), else null (the workspace cap). */
  participantCap: z.number().int().min(2).max(CALL_LIMITS.webinarMaxParticipants).nullable(),
  /** Increments on every state change; realtime events carry it so clients ignore stale ones. */
  version: z.number().int().nonnegative(),
});
export type CallDto = z.infer<typeof callDtoSchema>;

/**
 * `messages.metadata` of the call's system message (`kind = 'system'`, `subtype = 'call'`, D-039),
 * updated in place when the call ends: "Call · 12 min · Akshay, Mohit" or the missed-call notice.
 */
export const callMessageMetadataSchema = z.object({
  callId: uuidSchema,
  kind: callKindSchema,
  status: callStatusSchema,
  media: callMediaSchema,
  durationSec: z.number().int().nonnegative().nullable(),
  participantIds: z.array(uuidSchema),
});
export type CallMessageMetadata = z.infer<typeof callMessageMetadataSchema>;

// ---------------------------------------------------------------- LiveKit grant

/** One entry of `RTCConfiguration.iceServers`: TURN over UDP, TCP and TLS with REST credentials. */
export const iceServerSchema = z.object({
  urls: z.array(z.string()).min(1),
  username: z.string().optional(),
  credential: z.string().optional(),
});
export type IceServer = z.infer<typeof iceServerSchema>;

/**
 * What a client needs to connect to LiveKit (PLAN §4 `LiveKitGrant`). The token is only needed to
 * connect (10 minutes); `iceServers` carry time-limited TURN credentials and are passed to
 * `livekit-client` as `rtcConfig.iceServers`. A browser token never carries `roomAdmin`; moderation
 * is server-side (D-1029).
 */
export const liveKitGrantSchema = z.object({
  /** `LIVEKIT_URL` (wss). */
  url: z.string(),
  token: z.string(),
  roomName: z.string(),
  /**
   * `u_{userId}_{sessionId}` for a member's device (`identityKind = 'member'`), or
   * `g_{callGuestId}` for an outside guest's admitted device in a meeting room
   * (`identityKind = 'guest'`, D-1027). Guest grants are issued only by the public guest-session
   * route, after admission, for the live call (D-1028).
   */
  identity: z.string(),
  identityKind: callIdentityKindSchema,
  /**
   * What the token allows (D-1029): `full` (every call before wave 5, and presenters), `speaker`
   * (camera and microphone, no screen share) or `listener` (no publishing, no data channel, hidden
   * from other participants; the webinar audience).
   */
  profile: callGrantProfileSchema,
  expiresAt: isoDateTimeSchema,
  iceServers: z.array(iceServerSchema),
});
export type LiveKitGrant = z.infer<typeof liveKitGrantSchema>;

// ---------------------------------------------------------------- params and queries

export const callIdParamsSchema = z.object({ callId: uuidSchema });
/** D-1158: one participant of a call, for the moderator's "remove from call". */
export const callParticipantParamsSchema = z.object({ callId: uuidSchema, userId: uuidSchema });
export const callConversationParamsSchema = z.object({ conversationId: uuidSchema });

/** `GET /me/calls`: `status=missed` lists only the calls the caller missed. */
export const myCallsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(['missed']).optional(),
});
export type MyCallsQuery = z.infer<typeof myCallsQuerySchema>;

// ---------------------------------------------------------------- requests and responses

/** `GET /calls/availability`. Any signed-in member (guests included) may ask. */
export const callAvailabilityResponseSchema = z.object({
  available: z.boolean(),
  /** Null when `available`. */
  reason: callUnavailableReasonSchema.nullable(),
  maxParticipants: z.number().int().positive(),
  ringTimeoutSeconds: z.number().int().positive(),
});
export type CallAvailabilityResponse = z.infer<typeof callAvailabilityResponseSchema>;

/**
 * `POST /calls` (`Idempotency-Key` optional). `ringUserIds` is for channels only (a channel call
 * announces instead of ringing; naming people rings them): `422 ring_users_not_allowed` in a DM.
 */
export const startCallRequestSchema = z.object({
  conversationId: uuidSchema,
  media: callMediaSchema,
  ringUserIds: z.array(uuidSchema).max(CALL_LIMITS.maxRingInvitees).optional(),
});
export type StartCallRequest = z.infer<typeof startCallRequestSchema>;

/** `201 { call, livekit }`: the caller's own grant comes back with the call. */
export const startCallResponseSchema = z.object({
  call: callDtoSchema,
  livekit: liveKitGrantSchema,
});
export type StartCallResponse = z.infer<typeof startCallResponseSchema>;

/** `POST /calls/:callId/accept` → the callee's grant. */
export const acceptCallResponseSchema = startCallResponseSchema;
export type AcceptCallResponse = StartCallResponse;

/** `POST /calls/:callId/decline`. `busy` means already in another call. */
export const declineCallRequestSchema = z.object({
  reason: callDeclineReasonSchema.optional(),
});
export type DeclineCallRequest = z.infer<typeof declineCallRequestSchema>;

/** `POST /calls/:callId/ring`: ring more people, or ring someone again. */
export const ringCallRequestSchema = z.object({
  userIds: z.array(uuidSchema).min(1).max(CALL_LIMITS.maxRingInvitees),
});
export type RingCallRequest = z.infer<typeof ringCallRequestSchema>;

/** `GET /conversations/:conversationId/calls` and `GET /me/calls`. */
export const callListResponseSchema = paginated(callDtoSchema);
export type CallListResponse = z.infer<typeof callListResponseSchema>;

/** `GET /conversations/:conversationId/huddle`. */
export const huddleResponseSchema = z.object({ huddle: callDtoSchema.nullable() });
export type HuddleResponse = z.infer<typeof huddleResponseSchema>;

/** `POST /conversations/:conversationId/huddle/join` (audio unless `media` says video). */
export const joinHuddleRequestSchema = z.object({
  media: callMediaSchema.optional(),
});
export type JoinHuddleRequest = z.infer<typeof joinHuddleRequestSchema>;

export const joinHuddleResponseSchema = z.object({
  huddle: callDtoSchema,
  livekit: liveKitGrantSchema,
});
export type JoinHuddleResponse = z.infer<typeof joinHuddleResponseSchema>;

// ---------------------------------------------------------------- admin settings

/** `GET /admin/settings/calls` and the result of `PATCH` (the `calls.*` workspace settings). */
export const callSettingsSchema = z.object({
  enabled: z.boolean(),
  maxParticipants: z.number().int().min(2).max(CALL_LIMITS.maxParticipants),
  ringTimeoutSeconds: z
    .number()
    .int()
    .min(CALL_LIMITS.minRingTimeoutSeconds)
    .max(CALL_LIMITS.maxRingTimeoutSeconds),
});
export type CallSettings = z.infer<typeof callSettingsSchema>;

/** `PATCH /admin/settings/calls`: any subset of the settings, at least one. */
export const updateCallSettingsRequestSchema = callSettingsSchema
  .partial()
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Send at least one setting',
  });
export type UpdateCallSettingsRequest = z.infer<typeof updateCallSettingsRequestSchema>;

/** `POST /integrations/livekit/webhook` answers `200 { received: true }` (the body is LiveKit's). */
export const livekitWebhookResponseSchema = z.object({ received: z.literal(true) });
